import cx from "classnames";
import SearchBar from "components/SearchBar/SearchBar";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useParams } from "react-router-dom";
import { setShowClientFilesDrawer } from "reducers/drawer";
import { useGetFilesQuery } from "services/file-api";
import type { FilesTableDataType, FileType } from "types/DataTypes";
import { useToken } from "utils/context";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

const FILE_COLUMNS = [
  { key: "id", name: "ID", ...commonColumnProps },
  { key: "due_date", name: "Due Date", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps },
  { key: "services", name: "Services", ...commonColumnProps },
  { key: "claimant_name", name: "Claimant", ...commonColumnProps },
  { key: "dates_of_injury", name: "Date of Injury", ...commonColumnProps },
];

const ClientFilesDrawer = () => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { clientId } = useParams<{ clientId: string }>();
  const [page, setPage] = useState(1);
  const query = useQuery();

  const showClientFilesDrawer = useSelector(
    (state) => state.drawer.showClientFilesDrawer,
  );

  const {
    data: files = { results: [], count: 0 },
    isFetching,
    isLoading,
  } = useGetFilesQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      filters: {
        client_id: clientId,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      },
    },
    {
      skip: !accessToken || !clientId || !showClientFilesDrawer,
    },
  );

  const handleCellClick = (
    args: CellClickArgs<FilesTableDataType, unknown>,
  ) => {
    window.open(`/operations/files/${args.row.id}/edit`, "_blank");
  };

  const rows =
    files.results?.map((file: FileType) => ({
      id: file.id,
      due_date: file.due_date,
      datetime_closed: file.datetime_closed,
      client_name: file.client?.name,
      employer_name: file.employer?.name,
      is_rush_verbose: file.is_rush_verbose,
      status: file.status_verbose,
      services: [...new Set(file.services.map((service) => service.type))].join(
        ", ",
      ),
      claimant_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      date_of_birth: file.claimants[0]?.birth_date,
      dates_of_injury: file.dates_of_injury?.[0]?.date,
    })) || [];

  const isTableLoading =
    showClientFilesDrawer &&
    (isFetching || isLoading || !accessToken || !clientId);

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showClientFilesDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb">
          Client Files
          <a
            className="header-primary alt-btn"
            onClick={() => dispatch(setShowClientFilesDrawer(false))}
          >
            Close
          </a>
        </div>

        <div className="search-container">
          <SearchBar override={true} filterType="files" />
        </div>

        <div className="data-values mrg-top">
          <Table
            columns={FILE_COLUMNS}
            rows={rows}
            rowKeyGetter={(row: FilesTableDataType) => row.id}
            onCellClick={handleCellClick}
            isLoading={isTableLoading}
            drawer={true}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page,
              rowCount: files.count,
              onPageChange: (pageIndex) => setPage(pageIndex),
              isLoading: isTableLoading,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientFilesDrawer;
